import { formatSelection } from '@ms/components/betslip/core/save'
import { MAX_COUNT_BET_BUILDER } from '@ms/setting/betslip'
import { getEventCountMap, getBetCount } from '@ms/components/betslip/calculate.js'

// 获取 selection uof id, 用于下单
export const getUofId = (selection = {}) => {
  const { sportId, marketInfo, outcomeInfo } = selection
  const specifiers = marketInfo.specifiers ? `?${marketInfo.specifiers}` : ''
  return `uof:${marketInfo.product}/${sportId}/${marketInfo.id}/${outcomeInfo.id}${specifiers}`
}

// 是否是需要拒绝的 selection
export const isRejectedSelection = (selection = {}, target = {}) => {
  if (target.eventId !== selection.eventId) return false
  return getUofId(selection) === target.id
}

export const getSelectionId = ({ event, market, outcome }) => {
  let id = `${event.eventId}_${market.id}_${outcome.id}`
  const { specifiers } = market
  if (specifiers) {
    id += `_${specifiers}`
  }
  return id
}

export const getBetBuilderSelectionId = (eventId) => `${eventId}_betBuilder`

export const isInBetBuilderSelections = (selection, { event, market, outcome }) => {
  const subSelectionId = getSelectionId({ event, market, outcome })
  return selection.betBuilderSelections.some((x) => x.id === subSelectionId)
}

export const isSelectedOutcome = (selectionList = [], { event, market, outcome }) => {
  if (!market) return false
  const selection = convertSelectionOutcome({ outcome, market, event })
  const { id } = selection

  if (isBetBuilderSelection(selection)) {
    const targetSelection = selectionList.find((x) => x.id === id)
    if (!targetSelection) return false
    return isInBetBuilderSelections(targetSelection, { event, market, outcome })
  } else {
    return selectionList.some((x) => x.id === id)
  }
}

export const isSelectedSelection = (selectionList = [], selection = {}) => {
  if (!selection || selectionList.length === 0) return false
  const { id } = selection
  const targetSelection = selectionList.find((x) => x.id === id)

  if (isBetBuilderSelection(selection)) {
    if (!targetSelection) return false
    const { betBuilderSelections } = selection
    if (betBuilderSelections.length === 1) {
      const { marketInfo, outcomeInfo, ...event } = selection.betBuilderSelections[0]
      const subSelectionId = getSelectionId({ event, market: marketInfo, outcome: outcomeInfo })
      return targetSelection.betBuilderSelections.some((x) => x.id === subSelectionId)
    } else {
      return betBuilderSelections.every((x) => {
        const { marketInfo, outcomeInfo, ...event } = x
        const subSelectionId = getSelectionId({ event, market: marketInfo, outcome: outcomeInfo })
        return targetSelection.betBuilderSelections.some((y) => y.id === subSelectionId)
      })
    }
  } else {
    return !!targetSelection
  }
}

export const isSelectedCombosSelection = (selectionList = [], { event, combos }) => {
  if (!combos) return false
  const selection = convertComboSelection({ event, combos })
  const { id, betBuilderSelections } = selection
  const targetSelection = selectionList.find((x) => x.id === id)

  if (!targetSelection) return false
  return betBuilderSelections.every((x) => {
    return targetSelection.betBuilderSelections.some((selection) => x.id === selection.id)
  })
}

export const isBetBuilderSelection = (selection = {}) => /_betBuilder/.test(selection.id)

export const convertSelectionOutcome = ({ outcome, market, event }) => {
  if (!market) return
  if (!market.comboType) return formatSelection(outcome, market, event)
  const id = getBetBuilderSelectionId(event.eventId)
  const { markets, ...resetEvent } = event // eslint-disable-line
  const selection = {
    ...resetEvent,
    id,
    marketInfo: market || {},
    outcomeInfo: {
      // 初始值，避免干扰计算出现 NaN
      odds: 1,
      ...outcome
    },
    betBuilderSelections: [
      {
        ...resetEvent,
        id: getSelectionId({ event, market, outcome }),
        marketInfo: market,
        outcomeInfo: {
          isSuspended: false,
          isUnavailable: false,
          ...outcome
        }
      }
    ]
  }
  return selection
}

export const convertBetBuilderSelection = (itemSelction = '') => {
  const { event, market, outcome, subSelections = [] } = itemSelction
  const id = `${event.eventId}_betBuilder`
  const odds = subSelections.length === 1 ? subSelections[0].outcome.odds : outcome.odds
  const selection = {
    id,
    ...event,
    marketInfo: market,
    outcomeInfo: { ...outcome, odds },
    betBuilderSelections: subSelections.map((x) => {
      const { market, outcome } = x
      return {
        ...event,
        id: getSelectionId({ event, market, outcome }),
        marketInfo: market,
        outcomeInfo: {
          isSuspended: false,
          isUnavailable: false,
          ...outcome
        }
      }
    })
  }
  return selection
}

export const convertComboSelection = ({ event, combos }) => {
  const { markets, ...resetEvent } = event // eslint-disable-line
  const id = `${event.eventId}_betBuilder`
  const selection = {
    popularCombos: true,
    id,
    ...resetEvent,
    marketInfo: {},
    outcomeInfo: {
      isUnavailable: false,
      odds: combos.odds,
      probability: combos.probability
    },
    betBuilderSelections: combos.selections.map((x) => {
      const { outcomes, ...market } = x
      const outcome = outcomes[0]
      return {
        ...resetEvent,
        id: getSelectionId({ event, market, outcome }),
        marketInfo: market,
        outcomeInfo: {
          isSuspended: false,
          isUnavailable: false,
          ...outcome
        }
      }
    })
  }

  return selection
}

export const convertBettableSelections = (bettableBetSlip = []) => {
  return bettableBetSlip
    .map((x) => {
      const { event, market, outcome, subSelections } = x
      if (subSelections && subSelections.length) {
        const id = getBetBuilderSelectionId(event.eventId)
        return {
          id,
          ...convertBetBuilderSelection({ ...x, subSelections })
        }
      } else {
        const id = getSelectionId({ event, market, outcome })
        return {
          ...event,
          id,
          marketInfo: x.market,
          outcomeInfo: x.outcome
        }
      }
    })
    .filter((x) => {
      // 去重
      if (isBetBuilderSelection(x)) {
        return x.betBuilderSelections.length > 0
      }
      return true
    })
}

export const isBetBuilderSizeOverLimit = (selectionList, selection) => {
  if (!isBetBuilderSelection(selection)) return false
  const targetSelection = selectionList.find((x) => x.id === selection.id)
  if (!targetSelection) return false
  return targetSelection.betBuilderSelections.length >= MAX_COUNT_BET_BUILDER
}

export const hasAbnormalBetBuilderSelection = (selectionList = []) => {
  return selectionList.some((x) => {
    if (isBetBuilderSelection(x)) {
      return x.betBuilderSelections.some((subSelection) => {
        const { isSuspended, isUnavailable } = subSelection.outcomeInfo
        return isSuspended || isUnavailable
      })
    } else {
      return false
    }
  })
}

export const getCheckInfoBody = (selectionList) => {
  const body = []
  selectionList.forEach((x) => {
    if (isBetBuilderSelection(x)) {
      x.betBuilderSelections.forEach((subSelection) => {
        body.push(`${subSelection.eventId}::${getUofId(subSelection)}`)
      })
    } else {
      body.push(`${x.eventId}::${getUofId(x)}`)
    }
  })
  return body
}

/**
 * Calculates the total number of sub-selections within bet builder selections.
 *
 * @param {Array} selectionList - The list of selections to be processed.
 * @returns {number} The total number of sub-selections within bet builder selections.
 */
export const getBetBuilderSizeSubSelectionSize = (selectionList) => {
  return selectionList.filter(isBetBuilderSelection).reduce((sum, x) => {
    return sum + x.betBuilderSelections.length
  }, 0)
}

/**
 * Checks the temporary bet count by adding a new selection to the selection list.
 *
 * @param {Array} selections - The new selection list to be added.
 * @param {Array} selectionList - The current list of selections.
 * @returns {number} - The count of bets after adding the new selection.
 */
export const checkTempBetCount = (selections = [], selectionList) => {
  const tempList = [...selectionList, ...selections]
  const eventIds = tempList.map((x) => x.eventId)
  const eventCountMap = getEventCountMap(eventIds)
  const multipleLength = Object.keys(eventCountMap).length
  return getBetCount(tempList, multipleLength)
}
